"use client"

import type React from "react"
import { useEffect, useRef } from "react"
import { mouseStore } from "../mouseStore"

type TrailType = "dots" | "line" | "glow" | "ink" | "spark"

interface CursorTrailProps {
  /** Number of trail points (dots/line/glow) */
  count?: number
  /** CSS color string */
  color?: string
  /** Dot diameter / line width in px */
  size?: number
  /** z-index */
  zIndex?: number
  /**
   * "dots"  — fading circles (default)
   * "line"  — tapered Bézier brushstroke
   * "glow"  — soft radial-gradient halos
   * "ink"   — positions accumulate and fade over `fadeTime` ms
   * "spark" — each position spawns a drifting particle
   */
  type?: TrailType
  /** For "ink": ms before strokes fade out (default 1800) */
  fadeTime?: number
  /** For "spark": initial particle spread velocity in px/s (default 60) */
  spread?: number
}

export const CursorTrail: React.FC<CursorTrailProps> = ({
  count = 12,
  color = "rgba(255,255,255,0.6)",
  size = 8,
  zIndex = 9998,
  type = "dots",
  fadeTime = 1800,
  spread = 60,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const positionsRef = useRef<{ x: number; y: number }[]>([])
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resize()
    window.addEventListener("resize", resize, { passive: true })

    let cleanup: (() => void) | undefined

    // ── dots / line / glow — only redraw when positions change (dirty flag) ──
    if (type === "dots" || type === "line" || type === "glow") {
      let dirty = false

      const draw = () => {
        if (dirty) {
          ctx.clearRect(0, 0, canvas.width, canvas.height)
          const pts = positionsRef.current
          if (pts.length > 0) {
            if (type === "dots") drawDots(ctx, pts, color, size)
            else if (type === "line") drawLine(ctx, pts, color, size)
            else drawGlow(ctx, pts, color, size)
          }
          dirty = false
          // Schedule one more frame so the next dirty check can halt cleanly
          rafRef.current = requestAnimationFrame(draw)
        } else {
          // Nothing changed — idle. Canvas retains last drawn state.
          rafRef.current = null
        }
      }

      const unsub = mouseStore.subscribe((x, y) => {
        positionsRef.current = [{ x, y }, ...positionsRef.current.slice(0, count - 1)]
        dirty = true
        // Wake the RAF only when it has stopped
        if (rafRef.current === null) {
          rafRef.current = requestAnimationFrame(draw)
        }
      })

      cleanup = unsub
    }

    // ── ink — fade over time; RAF runs only while ink remains ────────────────
    if (type === "ink") {
      type InkPoint = { x: number; y: number; born: number }
      const ink: InkPoint[] = []

      const draw = (time: number) => {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        while (ink.length && time - ink[0].born > fadeTime) ink.shift()

        if (ink.length >= 2) {
          ctx.lineCap = "round"
          ctx.lineJoin = "round"

          const GAP_MS = 80
          let segStart = 0
          for (let i = 1; i <= ink.length; i++) {
            const atEnd = i === ink.length
            const hasGap = !atEnd && ink[i].born - ink[i - 1].born > GAP_MS

            if (atEnd || hasGap) {
              const pts = ink.slice(segStart, i)
              if (pts.length >= 2) {
                const age = time - pts[0].born
                const alpha = Math.pow(Math.max(0, 1 - age / fadeTime), 1.5)

                ctx.beginPath()
                ctx.moveTo(pts[0].x, pts[0].y)
                for (let j = 1; j < pts.length - 1; j++) {
                  const mx = (pts[j].x + pts[j + 1].x) / 2
                  const my = (pts[j].y + pts[j + 1].y) / 2
                  ctx.quadraticCurveTo(pts[j].x, pts[j].y, mx, my)
                }
                ctx.lineTo(pts[pts.length - 1].x, pts[pts.length - 1].y)

                ctx.strokeStyle = color
                ctx.lineWidth = Math.max(size * 0.85, 1)
                ctx.globalAlpha = alpha
                ctx.stroke()
              }
              segStart = i
            }
          }
        }

        ctx.globalAlpha = 1
        // Keep running only while there is ink left to fade
        if (ink.length > 0) {
          rafRef.current = requestAnimationFrame(draw)
        } else {
          rafRef.current = null
        }
      }

      const unsub = mouseStore.subscribe((x, y) => {
        ink.push({ x, y, born: performance.now() })
        if (rafRef.current === null) {
          rafRef.current = requestAnimationFrame(draw)
        }
      })

      cleanup = unsub
    }

    // ── spark — physics simulation; RAF runs only while sparks are alive ──────
    if (type === "spark") {
      type Spark = { x: number; y: number; vx: number; vy: number; born: number }
      const sparks: Spark[] = []
      let lastTime = performance.now()
      const LIFETIME = fadeTime
      const GRAVITY = 120

      const draw = (time: number) => {
        const dt = Math.min((time - lastTime) / 1000, 0.05)
        lastTime = time
        ctx.clearRect(0, 0, canvas.width, canvas.height)

        for (let i = sparks.length - 1; i >= 0; i--) {
          const s = sparks[i]
          const age = time - s.born
          if (age > LIFETIME) { sparks.splice(i, 1); continue }

          s.x += s.vx * dt
          s.y += s.vy * dt
          s.vy += GRAVITY * dt

          const t = 1 - age / LIFETIME
          const r = Math.max(size * t * 0.45, 0.5)
          ctx.beginPath()
          ctx.arc(s.x, s.y, r, 0, Math.PI * 2)
          ctx.fillStyle = color
          ctx.globalAlpha = t
          ctx.fill()
        }
        ctx.globalAlpha = 1

        if (sparks.length > 0) {
          rafRef.current = requestAnimationFrame(draw)
        } else {
          rafRef.current = null
        }
      }

      const unsub = mouseStore.subscribe((x, y) => {
        const angle = Math.random() * Math.PI * 2
        sparks.push({
          x, y,
          vx: Math.cos(angle) * spread * (0.5 + Math.random()),
          vy: Math.sin(angle) * spread * (0.5 + Math.random()) - spread * 0.4,
          born: performance.now(),
        })
        if (rafRef.current === null) {
          rafRef.current = requestAnimationFrame(draw)
        }
      })

      cleanup = unsub
    }

    return () => {
      window.removeEventListener("resize", resize)
      cleanup?.()
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [color, count, size, type, fadeTime, spread])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ position: "fixed", top: 0, left: 0, pointerEvents: "none", zIndex }}
    />
  )
}

// ─── draw modes ───────────────────────────────────────────────────────────────

function drawDots(ctx: CanvasRenderingContext2D, pts: { x: number; y: number }[], color: string, size: number) {
  for (let i = 0; i < pts.length; i++) {
    const t = 1 - i / pts.length
    ctx.beginPath()
    ctx.arc(pts[i].x, pts[i].y, Math.max(size * t * 0.5, 0.5), 0, Math.PI * 2)
    ctx.fillStyle = color
    ctx.globalAlpha = t
    ctx.fill()
  }
  ctx.globalAlpha = 1
}

function drawLine(ctx: CanvasRenderingContext2D, pts: { x: number; y: number }[], color: string, size: number) {
  if (pts.length < 2) return
  for (let i = 0; i < pts.length - 1; i++) {
    const t = 1 - i / pts.length
    ctx.beginPath()
    ctx.moveTo(pts[i].x, pts[i].y)
    if (i < pts.length - 2) {
      const mx = (pts[i + 1].x + pts[i + 2].x) / 2
      const my = (pts[i + 1].y + pts[i + 2].y) / 2
      ctx.quadraticCurveTo(pts[i + 1].x, pts[i + 1].y, mx, my)
    } else {
      ctx.lineTo(pts[i + 1].x, pts[i + 1].y)
    }
    ctx.strokeStyle = color
    ctx.globalAlpha = t
    ctx.lineWidth = Math.max(size * t, 0.5)
    ctx.lineCap = "round"
    ctx.lineJoin = "round"
    ctx.stroke()
  }
  ctx.globalAlpha = 1
}

function drawGlow(ctx: CanvasRenderingContext2D, pts: { x: number; y: number }[], color: string, size: number) {
  for (let i = 0; i < pts.length; i++) {
    const t = 1 - i / pts.length
    const r = Math.max(size * t * 2, 0.5)
    const { x, y } = pts[i]
    const grad = ctx.createRadialGradient(x, y, 0, x, y, r)
    grad.addColorStop(0, color)
    grad.addColorStop(1, "rgba(0,0,0,0)")
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fillStyle = grad
    ctx.globalAlpha = t
    ctx.fill()
  }
  ctx.globalAlpha = 1
}
